import { useDispatch, useSelector } from 'react-redux';
import { Button, BUTTON_SIZES, BUTTON_VARIANTS } from 'ui/button';
import { Modal, ModalHeader, ModalBody, ModalFooter, MODAL_SIZES, MODAL_VARIANTS } from 'ui/modal';
import {
  closeModal,
  getEdges,
  getNodes,
  getWorkflow,
  fetchWorkflowRequest,
  saveWorkflowDraftRequest,
} from '../../../ducks';
import { DRAFT_HAS_UNSAVED_CHANGES_MODAL } from '../../../constants';
import { WorkflowType } from '../../../enums';

export const DONT_SAVE_BUTTON_TEST_ID = 'dont-save-button';
export const SAVE_AND_SWITCH_BUTTON_TEST_ID = 'save-and-switch-button';

export interface DraftHasUnsavedChangesModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Selected workflow id */
  workflowId: number;
  /** Selected workflow type */
  workflowType: WorkflowType;
}

export const DraftHasUnsavedChangesModal: React.FC<
  React.PropsWithChildren<DraftHasUnsavedChangesModalProps>
> = ({ workflowId, workflowType, isOpen }) => {
  const dispatch = useDispatch();
  const workflow = useSelector(getWorkflow);
  const nodes = useSelector(getNodes);
  const edges = useSelector(getEdges);

  const handleCloseModal = () => dispatch(closeModal(DRAFT_HAS_UNSAVED_CHANGES_MODAL));

  const handleGoToPublished = () => {
    dispatch(fetchWorkflowRequest(workflowId, workflowType));
    handleCloseModal();
  };

  const handleSaveAndGoToPublished = () => {
    dispatch(
      saveWorkflowDraftRequest(
        { ...workflow, nodes, edges },
        {
          id: workflowId,
          workflowType,
        },
      ),
    );
    handleCloseModal();
  };

  return (
    <Modal
      id={DRAFT_HAS_UNSAVED_CHANGES_MODAL}
      isModalOpen={isOpen}
      onClose={handleCloseModal}
      size={MODAL_SIZES.SMALL}
      variant={MODAL_VARIANTS.WARNING}
    >
      <ModalHeader autoFocus={false}>Unsaved changes</ModalHeader>
      <ModalBody>
        <p>This journey has been edited. Do you want to save your changes before switching?</p>
      </ModalBody>
      <ModalFooter>
        <Button
          data-test={DONT_SAVE_BUTTON_TEST_ID}
          onClick={handleGoToPublished}
          size={BUTTON_SIZES.LARGE}
          variant={BUTTON_VARIANTS.SECONDARY}
        >
          Leave without saving
        </Button>
        <Button
          data-test={SAVE_AND_SWITCH_BUTTON_TEST_ID}
          onClick={handleSaveAndGoToPublished}
          size={BUTTON_SIZES.LARGE}
          variant={BUTTON_VARIANTS.PRIMARY}
        >
          Save and switch
        </Button>
      </ModalFooter>
    </Modal>
  );
};
